import { FC } from "react";
import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import { QUOTA_TYPES, QuotaKey } from "constants/quotaTypes";

type QuotaValues = Partial<Record<QuotaKey, number | string>>;

interface QuotaListProps {
  /** Any object carrying the four numeric quota fields (Plan / IPlan / subscription). */
  plan: QuotaValues;
  /** `card` = highlighted band for pricing/lifetime cards; `compact` = tight rows for mobile. */
  variant?: "card" | "compact";
  /** Show the one-line description under each quota label. */
  showDescriptions?: boolean;
  /** Optional heading shown above the rows. */
  title?: string;
  /** Optional suffix appended to each quota number (e.g. "/mo"). */
  valueSuffix?: string;
}

const BRAND = "#185ADB";

const QuotaList: FC<QuotaListProps> = ({
  plan,
  variant = "card",
  showDescriptions = false,
  title = "Plan Credits — per month",
  valueSuffix = "",
}) => {
  const isCard = variant === "card";

  return (
    <Box
      w="100%"
      borderRadius="14px"
      bg={isCard ? "#F5F8FF" : "transparent"}
      border={isCard ? "1px solid #DCE6FF" : "none"}
      p={isCard ? "16px" : "0px"}
    >
      <Text
        fontSize="11px"
        fontWeight={700}
        letterSpacing="0.08em"
        textTransform="uppercase"
        color={BRAND}
        mb={3}
      >
        {title}
      </Text>

      <Flex flexDir="column" gap={isCard ? 3 : 2}>
        {QUOTA_TYPES.map(({ key, label, description, Icon: QuotaIcon }) => (
          <Flex key={key} align={showDescriptions ? "flex-start" : "center"} gap={3}>
            <Flex
              align="center"
              justify="center"
              flexShrink={0}
              w="32px"
              h="32px"
              borderRadius="8px"
              bg="#E8EFFF"
              color={BRAND}
            >
              <Icon as={QuotaIcon} boxSize="18px" />
            </Flex>

            <Box flex="1" minW={0}>
              <Text fontSize="14px" fontWeight={600} color="#18181B" lineHeight="1.2">
                {label}
              </Text>
              {showDescriptions && (
                <Text fontSize="12px" color="#6B7280" mt={0.5} lineHeight="1.3">
                  {description}
                </Text>
              )}
            </Box>

            <Flex
              align="center"
              justify="center"
              flexShrink={0}
              minW="44px"
              h="26px"
              px={2.5}
              borderRadius="999px"
              bg={BRAND}
              color="#fff"
            >
              <Text fontSize="13px" fontWeight={700} whiteSpace="nowrap">
                {Number(plan?.[key] ?? 0).toLocaleString()}{valueSuffix}
              </Text>
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
};

export default QuotaList;
